/**
 * FULL block registry — the complete 53-block production set:
 * the 8 fully-typed core blocks (registry.ts) + the 45 extended blocks
 * (registry-extended.ts). Parity-gated against the legacy monolith:
 * `npm run test:parity` renders every block with the monolith's own default
 * data through BOTH implementations and requires byte-identical output.
 */
import { REGISTRY } from "./registry";
import { REGISTRY_EXT, type ExtBlockDefinition } from "./registry-extended";

export type AnyBlockDefinition = ExtBlockDefinition;

export const FULL_REGISTRY: Record<string, AnyBlockDefinition> = {
  ...(REGISTRY as unknown as Record<string, AnyBlockDefinition>),
  ...REGISTRY_EXT,
};

/* Title-case every block name for display (monolith L7247–7249, 2026-07-24 per
   Jack — "Hero Image", "Logo Header"…). The monolith mutates M.label at runtime
   AFTER the defs, so the registry source stays byte-equal to `M` while the
   DISPLAYED labels are title-cased everywhere (inspector header, canvas tag,
   palette, gap menu, toasts). Safe: nothing compares label strings. */
const __tc = (s: unknown) => String(s ?? "").replace(/\b\w/g, (c) => c.toUpperCase());
try {
  Object.keys(FULL_REGISTRY).forEach((k) => {
    const d = FULL_REGISTRY[k];
    if (d && d.label) (d as { label: string }).label = __tc(d.label);
  });
} catch { /* noop — display nicety only */ }

/* Surface ISC under a visible tab (Content) since MVP hides the People tab
   (monolith L7251, MVP branch — the only mode since the 2026-08-09 cutover). */
try { if (FULL_REGISTRY.isc) (FULL_REGISTRY.isc as { grp: string }).grp = "Content blocks"; } catch { /* noop */ }

/** palette order — mirrors the legacy GRP_ORDER.
 *  "SFMC Blocks" is the group `__echRegister` puts every one of Layne's real
 *  Content Builder blocks in (services/echRegistry.ts). It has to be listed here
 *  or the palette's `GRP_ORDER.map(...)` loop simply never reaches them. */
export const GRP_ORDER = [
  "SFMC Blocks",
  "Structure",
  "Content blocks",
  "Interactive Modules",
  "Property",
  "Brand & value",
  "Offer & finance",
  "Events",
  "Actions",
  "People",
] as const;

export const ALL_TYPES = Object.keys(FULL_REGISTRY);
export const VISIBLE_TYPES = ALL_TYPES.filter((k) => !FULL_REGISTRY[k].hidden);

/**
 * Live palette list. The constants above snapshot the registry at MODULE LOAD,
 * which was fine while it was static — but ECH blocks arrive from SFMC after a
 * fetch and register themselves into FULL_REGISTRY (monolith `M[key]=…`), so a
 * snapshot can never contain them. Anything rendering the palette must call this.
 */
export function visibleTypes(): string[] {
  return Object.keys(FULL_REGISTRY).filter((k) => !FULL_REGISTRY[k].hidden);
}

/** a block definition by type, ECH blocks included (they live in the same map) */
export function blockDef(type: string): AnyBlockDefinition | undefined {
  return FULL_REGISTRY[type];
}

/**
 * Display label for a block type, safe before registration.
 *
 * An ECH type (`sfmc_<contentId>`) only enters the registry once /sfmc-blocks
 * resolves, so a document restored from a favorite / a saved build can legitimately
 * hold a type the registry does not know YET. Every call site that read
 * `FULL_REGISTRY[b.type].label` threw on that — caught by StudioView.ech.test.tsx,
 * which places an ECH block before the fetch lands.
 */
export function blockLabel(type: string): string {
  const d = FULL_REGISTRY[type];
  return (d && d.label) || (/^sfmc_/.test(type) ? "Marketing Cloud block" : type);
}
